import { Thread } from '@langchain/langgraph-sdk'
import { t } from '@lingui/macro'
import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState, useMemo } from 'react'
import { useAuthenticatedClient } from './client'
import { jwtDecode } from 'jwt-decode'
import { OpenAPI } from '@om1/falcon-api'

interface ThreadContextType {
    getThreads: () => Promise<Thread[]>
    threads: Thread[]
    setThreads: Dispatch<SetStateAction<Thread[]>>
    threadsLoading: boolean
    setThreadsLoading: Dispatch<SetStateAction<boolean>>
}

const ThreadContext = createContext<ThreadContextType | undefined>(undefined)

// Separate hook for user authentication information
export function useCurrentUser() {
    // Get token directly like useAuthenticatedClient does
    const token = OpenAPI.TOKEN ? String(OpenAPI.TOKEN) : null

    // Decode the user ID from the token
    const userId = useMemo(() => {
        try {
            if (!token) return null

            const decodedToken = jwtDecode<{ sub: string }>(token)
            return decodedToken.sub
        } catch (error) {
            console.error(t`Error decoding token:`, error)
            return null
        }
    }, [token]) // Include token as a dependency

    return { userId }
}

export function ThreadProvider({ children, apiUrl = '' }: { children: ReactNode; apiUrl?: string }) {
    const [threads, setThreads] = useState<Thread[]>([])
    const [threadsLoading, setThreadsLoading] = useState(false)
    const client = useAuthenticatedClient(apiUrl)
    const { userId } = useCurrentUser()

    const getThreads = useCallback(async (): Promise<Thread[]> => {
        if (!apiUrl || !client || !userId) return []

        try {
            // Filter threads to only show those created by the current user
            const searchParams = {
                limit: 100,
                metadata: { owner: userId }
            }

            const threads = await client.threads.search(searchParams)
            return threads
        } catch (error) {
            console.error(t`Error fetching all threads:`, error)
            return []
        }
    }, [apiUrl, client, userId])

    const value = {
        getThreads,
        threads,
        setThreads,
        threadsLoading,
        setThreadsLoading
    }

    return <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
}

export function useThreads() {
    const context = useContext(ThreadContext)
    if (context === undefined) {
        throw new Error(t`No thread found in context. Please ensure you are using the ThreadProvider.`)
    }
    return context
}
